import { ErrorEventsEnum } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

/**
 * Represents an external audio device detected on the system.
 */
export interface IExternalDevice {
  /** The type of audio device (audioinput or audiooutput) */
  kind: MediaDeviceKind;
  /** The device label/name */
  label: string;
}

/**
 * Keywords that indicate an external audio device.
 */
const EXTERNAL_DEVICE_KEYWORDS = [
  'headphone',
  'headset',
  'bluetooth',
  'bt',
  'usb',
  'external',
  'wireless',
  'airpods',
  'earbuds',
  'earphone',
  'ear piece',
  'earbud',
  'external speaker',
  'usb speaker',
  'bluetooth speaker',
  'external microphone',
  'usb microphone',
  'bluetooth microphone',
  'dongle',
  'adapter',
  'jack',
  '3.5mm',
  'aux',
];

/**
 * Keywords that indicate a built-in device.
 */
const BUILT_IN_KEYWORDS = [
  'default',
  'built-in',
  'builtin',
  'internal',
  'integrated',
  'speaker',
  'speakers',
  'microphone',
  'mic',
  'earpiece',
  'receiver',
  'front',
  'back',
  'bottom',
  'top',
];

/**
 * Regex patterns that match built-in mobile device naming conventions.
 */
export const BUILT_IN_PATTERNS = [
  /^speaker\s*\d*$/i,
  /^speakers\s*\d*$/i,
  /^microphone\s*\d*$/i,
  /^mic\s*\d*$/i,
  /^earpiece\s*\d*$/i,
  /^receiver\s*\d*$/i,
  /^front\s+(mic|microphone|speaker|speakers)$/i,
  /^back\s+(mic|microphone|speaker|speakers)$/i,
  /^top\s+(mic|microphone|speaker|speakers)$/i,
  /^bottom\s+(mic|microphone|speaker|speakers)$/i,
  /^(left|right)\s+(speaker|speakers|mic|microphone)$/i,
  /^(primary|secondary)\s+(speaker|speakers|mic|microphone)$/i,
  /^(built[- ]?in|internal|integrated)\s+(speaker|speakers|mic|microphone|earpiece|receiver)$/i,
  /built[- ]?in\s*(receiver|speaker|microphone)/i,
  /speakerphone/i,
  /^(phone|mobile|device)\s+(speaker|speakers|mic|microphone|earpiece|receiver)$/i,
];

/**
 * Regex patterns that match external audio device naming conventions.
 */
export const EXTERNAL_DEVICE_PATTERNS = [
  /bluetooth/i,
  /\bbt\b/i,
  /headphone|headphones/i,
  /earphone|earphones/i,
  /earbud|earbuds/i,
  /neckband/i,
  /airpods/i,
  /buds/i,
  /galaxy\s*buds/i,
  /sony|jbl|bose|boat|realme|oppo|oneplus/i,
  /wired/i,
  /usb/i,
  /type[- ]?c/i,
  /aux/i,
  /3\.5\s*mm/i,
  /hands[- ]?free/i,
  /car\s*audio/i,
  /automotive/i,
  /communications/i,
  /system\s*default/i,
];

/**
 * Application keywords that should be allowed (not treated as external devices).
 *
 * Microsoft Teams and similar communication applications create virtual audio devices
 * when running (e.g., "Microsoft Teams Audio", "Teams Microphone", etc.) that appear
 * in the system's audio device list. These virtual devices are not physical external
 * hardware and should not trigger the external device detection warning during
 * audio assessment tests.
 *
 * Since most team members use Microsoft Teams for daily communication, excluding
 * these virtual devices prevents false positives that would incorrectly block users
 * from proceeding with the device assessment. The virtual devices are part of the
 * application's audio routing system and do not interfere with the actual hardware
 * audio assessment being performed.
 */
const EXCLUDED_APPLICATIONS = [
  'microsoft teams',
  'teams',
  'ms teams',
  'microsoft',
];

/**
 * Determines if a device label indicates an external audio device.
 * Prioritizes built-in device detection to ensure mobile devices work correctly.
 */
function isExternalDevice(label: string): boolean {
  if (!label || label.trim() === '') return false;

  const lowerLabel = label.toLowerCase();
  const trimmedLabel = label.trim();

  const containsExcludedApp = (app: string): boolean =>
    lowerLabel.includes(app);

  const isExcludedApp = EXCLUDED_APPLICATIONS.some(containsExcludedApp);

  if (isExcludedApp) return false;

  if (
    lowerLabel.includes('earpiece') ||
    lowerLabel.includes('receiver') ||
    trimmedLabel.toLowerCase() === 'earpiece' ||
    trimmedLabel.toLowerCase() === 'receiver'
  ) {
    return false;
  }

  const testBuiltInPattern = (pattern: RegExp): boolean =>
    pattern.test(trimmedLabel);

  const matchesBuiltInPattern = BUILT_IN_PATTERNS.some(testBuiltInPattern);

  if (matchesBuiltInPattern) return false;

  const hasBuiltInIndicator =
    lowerLabel.includes('built-in') ||
    lowerLabel.includes('builtin') ||
    lowerLabel.includes('internal') ||
    lowerLabel.includes('integrated') ||
    lowerLabel.includes('default');

  if (hasBuiltInIndicator) return false;

  const testExternalPattern = (pattern: RegExp): boolean =>
    pattern.test(lowerLabel);

  const matchesExternalPattern =
    EXTERNAL_DEVICE_PATTERNS.some(testExternalPattern);

  if (matchesExternalPattern) return true;

  const containsExternalKeyword = (keyword: string): boolean =>
    lowerLabel.includes(keyword);

  const matchesExternalKeyword = EXTERNAL_DEVICE_KEYWORDS.some(
    containsExternalKeyword
  );

  const containsBuiltInKeyword = (keyword: string): boolean =>
    lowerLabel.includes(keyword);

  const matchesBuiltInKeyword = BUILT_IN_KEYWORDS.some(containsBuiltInKeyword);

  if (matchesExternalKeyword) {
    const isSimpleBuiltInName =
      /^(speaker|speakers|mic|microphone)\s*\d*$/i.test(trimmedLabel) ||
      /^(front|back|top|bottom|left|right|primary|secondary)\s+(speaker|speakers|mic|microphone)$/i.test(
        trimmedLabel
      );

    if (isSimpleBuiltInName) return false;

    if (matchesBuiltInKeyword) return true;

    return true;
  }

  if (matchesBuiltInKeyword) return false;

  return false;
}

/**
 * Detects external audio devices connected to the system.
 * Only returns devices that are explicitly external (headphones, Bluetooth, USB, etc.).
 * Built-in mobile components (speakers, microphones, earpiece) are excluded.
 *
 * Requires microphone permission to access device labels. If permission is denied
 * or the API is unavailable, returns an empty array.
 *
 * @returns Promise resolving to an array of external devices, or empty array
 * if none found, permission denied, or API unavailable
 */
export async function detectExternalAudioDevices(): Promise<IExternalDevice[]> {
  try {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.enumerateDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      logErrorToSentry({
        error: new Error('MediaDevices API not available'),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'MEDIADEVICES_API_UNAVAILABLE',
        source: 'detectExternalAudioDevices',
      });

      return [];
    }

    let stream: MediaStream | null = null;

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      logErrorToSentry({
        error: error instanceof Error ? error : new Error(String(error)),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'MIC ACCESS DENIED',
        source: 'detectExternalAudioDevices',
      });

      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioDevices = devices.filter(
        (device) =>
          device.kind === 'audioinput' || device.kind === 'audiooutput'
      );

      const externalDevices: IExternalDevice[] = [];

      audioDevices.forEach((device) => {
        const label = device.label || '';

        if (isExternalDevice(label)) {
          externalDevices.push({
            kind: device.kind,
            label,
          });
        }
      });

      return externalDevices;
    } finally {
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }
    }
  } catch {
    logErrorToSentry({
      error: new Error('Failed to detect external audio devices'),
      errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
      errorName: 'EXTERNAL_AUDIO_DEVICES_DETECTION_FAILED',
      source: 'detectExternalAudioDevices',
    });

    return [];
  }
}
